import { resolveHttpResourceUrl } from "../../../lib/http/resourceUrl";
/* eslint-disable jsdoc/require-jsdoc -- 辅助函数逐项映射旧版 ASPX 模板结构与计算规则。 */
import { forwardRef, useMemo } from "react";

import { MarkdownContent } from "../../../shared/ui";
import type {
  PublicItinerary,
  PublicItineraryNode,
  PublicTimeSlot,
} from "../types";
import { ITINERARY_SERVICE_STANDARD_TYPES } from "../serviceStandardTypes";
import { buildDayNarratives } from "../service/itineraryOutputService";
import { ItineraryMarkdown, ItineraryMarkdownProvider } from "./ItineraryMarkdown";
import "./itineraryLegacyPdf.css";

type Props = { itinerary: PublicItinerary; includeDayImages?: boolean };
type PublicDay = PublicItinerary["days"][number];
type OverviewRow = {
  firstDay: PublicDay;
  firstDayNumber: number;
  lastDayNumber: number;
  points: PublicItineraryNode[];
};

const ASSET_ROOT = "/assets/itinerary-pdf-legacy";
const OVERVIEW_CONTENT_WIDTH = 783;
const OVERVIEW_ROW_HEIGHT = 66;
const OVERVIEW_FIXED_PAGE_LIMIT = 1793;
const OVERVIEW_WITH_IMAGE_LIMIT = 957;
const GALLERY_SIGNS = ["A", "B", "C", "D", "D", "D"] as const;
const PDF_CODE_FENCE = /^\s*(```+|~~~+)/;

/** 将 PDF Markdown 正文的普通单换行改为硬换行，代码块与空行保持原样。 */
function pdfLineBreaks(content: string): string {
  let codeFence: string | null = null;
  return content
    .replace(/<br\s*\/?\s*>[ \t]*\r?\n[ \t]*/gi, "<br />")
    .split(/\r?\n/).map((line, index, lines) => {
    const fence = line.match(PDF_CODE_FENCE)?.[1];
    if (fence) {
      if (!codeFence) codeFence = fence.slice(0, 3);
      else if (fence.startsWith(codeFence)) codeFence = null;
      return line;
    }
    if (codeFence || !line.trim() || /<br\s*\/?\s*>[ \t]*$/i.test(line) || index === lines.length - 1) return line;
    return `${line.replace(/[ \t]+$/, "")}  `;
    }).join("\n");
}
const LEGACY_SLOT_ORDER: PublicTimeSlot[] = ["morning", "afternoon", "evening", "unscheduled"];
const LEGACY_SLOT_LABELS: Partial<Record<PublicTimeSlot, string>> = {
  morning: "上午",
  afternoon: "下午",
  evening: "晚上",
  unscheduled: "未分时",
};

/** 旧版 RoutePdf_HasPic.aspx 的 type=4 只读文档。 */
export const ItineraryPdfDocument = forwardRef<HTMLElement, Props>(function ItineraryPdfDocument(
  { itinerary, includeDayImages = true },
  ref,
) {
  const legacyItinerary = itinerary;
  const overviewRows = useMemo(() => buildOverviewRows(legacyItinerary.days), [legacyItinerary.days]);
  const overviewHeight = estimateOverviewHeight(overviewRows);
  const overviewFlows = overviewHeight > OVERVIEW_FIXED_PAGE_LIMIT;
  const showOverviewImage = overviewHeight <= OVERVIEW_WITH_IMAGE_LIMIT;
  const [coverImage, overviewImage] = legacyHeaderImages(
    legacyItinerary.title ?? "",
    legacyItinerary.pdfHeadImageUrls,
  );
  const serviceEntries = ITINERARY_SERVICE_STANDARD_TYPES.map(([key, label]) => ({
    key,
    label,
    items: (legacyItinerary.customerServiceStandards?.[key] ?? [])
      .map((item) => item.trim())
      .filter(Boolean),
  })).filter(({ items }) => items.length > 0);

  return (
    <ItineraryMarkdownProvider itinerary={legacyItinerary} annotatePointCategories>
      <article ref={ref} id="page-content" className="a4-wp-all" aria-label="客户行程 PDF 文档">
        <section className="a4-wp a4p1" data-pdf-section="cover">
          <div className="a4ewm_right">
            <img src={`${ASSET_ROOT}/brand-qr.png`} alt="花儿艺术旅行二维码" />
            <p>扫一扫，了解花儿艺术旅行</p>
          </div>
          <div className="imgs_logo">
            <img src={`${ASSET_ROOT}/A4_logo.jpg`} alt="花儿艺术旅行" />
          </div>
          <div className="imgs_pic" aria-hidden="true">
            <div className="mask_pic"><img src={`${ASSET_ROOT}/A4_mask.png`} alt="" /></div>
            <div className="con_pic"><img src={coverImage} alt="" /></div>
          </div>
          <div className="title">
            {legacyItinerary.title || "客户行程"}
            <br />
            <img src={`${ASSET_ROOT}/title-divider.jpg`} alt="" />
          </div>
          <div className="con">
            {legacyItinerary.startDate && legacyItinerary.endDate
              ? <>出行时间：{legacyItinerary.startDate}~{legacyItinerary.endDate}</>
              : null}
            <br />
          </div>
          <div className="a4bottominfo" aria-hidden="true">
            <img src={`${ASSET_ROOT}/bottom-brand.jpg`} alt="" />
          </div>
        </section>

        {!overviewFlows ? (
          <section className="a4-wp a4p2" data-pdf-section="overview">
            {showOverviewImage ? <LegacyPageImage image={overviewImage} /> : null}
            <div className="a4topinfo" aria-hidden="true">
              <img src={`${ASSET_ROOT}/A4_03.jpg`} alt="" />
            </div>
            <div className="quy-wp">
              <LegacySectionTitle icon="kehu-4.png" title="行程概述" subtitle={legacyItinerary.title} />
              <OverviewTable rows={overviewRows} />
            </div>
          </section>
        ) : null}

        <div className="a4-wp-f2">
          {overviewFlows ? (
            <section data-pdf-section="overview">
              <div className="a4topinfo-f2" aria-hidden="true">
                <img src={`${ASSET_ROOT}/A4_03.jpg`} alt="" />
              </div>
              <LegacySectionTitle icon="kehu-4.png" title="行程概述" subtitle={legacyItinerary.title} />
              <div className="quy-wp-p1"><OverviewTable rows={overviewRows} /></div>
            </section>
          ) : null}

          <div className="box10" />
          <LegacySectionTitle icon="kehu-3.png" title="行程详情" />
          <section className="quy-wp-p2" data-pdf-section="details">
            {legacyItinerary.days.map((day) => <LegacyDay key={day.dayNumber} day={day} includeDayImages={includeDayImages} />)}
          </section>

          {serviceEntries.length ? (
            <section data-pdf-section="services">
              <div className="box50" />
              <LegacySectionTitle icon="xl18-2.png" title="服务标准" />
              <div className="fbz-wp-p4 text-30px">
                {serviceEntries.map(({ key, label, items }) => (
                  <div className="legacy-service-entry" key={key}>
                    <strong>{label}</strong>
                    {items.map((item, index) => (
                      <MarkdownContent content={pdfLineBreaks(item)} key={`${key}-${index}`} />
                    ))}
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {legacyItinerary.customerNotices?.trim() ? (
            <section data-pdf-section="notices">
              <div className="box50" />
              <LegacySectionTitle icon="xl18-4.png" title="注意事项" />
              <div className="fbz-wp-p4 text-30px">
                <ItineraryMarkdown content={pdfLineBreaks(legacyItinerary.customerNotices)} />
              </div>
            </section>
          ) : null}
        </div>
        <div className="box50" />
      </article>
    </ItineraryMarkdownProvider>
  );
});

function LegacySectionTitle({ icon, title, subtitle }: { icon: string; title: string; subtitle?: string | null }) {
  return (
    <div className="a4ptit-f2">
      <span className="iimg"><img src={`${ASSET_ROOT}/${icon}`} alt="" /></span>
      <span className="tit">{title}</span>
      {subtitle ? <span className="tit2">{subtitle}</span> : null}
    </div>
  );
}

function LegacyPageImage({ image }: { image: string }) {
  return (
    <div className="imgs_pic" aria-hidden="true">
      <div className="mask_pic"><img src={`${ASSET_ROOT}/A4_mask.png`} alt="" /></div>
      <div className="con_pic"><img src={image} alt="" /></div>
    </div>
  );
}

function OverviewTable({ rows }: { rows: OverviewRow[] }) {
  return (
    <div className="xc-table-wp">
      <div className="xc-table" role="table" aria-label="行程概述">
        {rows.map((row) => (
          <div className="tr-wp" role="row" key={`${row.firstDayNumber}-${row.lastDayNumber}`}>
            <div className="td-t td1" role="cell" style={{ width: "20%" }}>
              <span className="dds">
                {overviewDayLabel(row)}
                <small className="date"> {overviewDayDate(row.firstDay.dayDate)}</small>
              </span>
            </div>
            <div className="td-t td2" role="cell" style={{ width: "18%" }}>
              <span className="dzs">{accommodationForDay(row.firstDay)}<i className="a4xcap-zhus" /></span>
            </div>
            <div className="td-t td3" role="cell" style={{ width: "62%" }}>
              <OverviewPlan row={row} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OverviewPlan({ row }: { row: OverviewRow }) {
  if (!row.points.length) return <div className="xc-tablexclx">{legacyDayTitle(row.firstDay)}</div>;
  const pointsBySlot = LEGACY_SLOT_ORDER.map((slot) => ({
    slot,
    points: row.points.filter((point) => point.timeSlot === slot),
  })).filter(({ points }) => points.length > 0);
  const hasMorningAndAfternoon = pointsBySlot.some(({ slot }) => slot === "morning")
    && pointsBySlot.some(({ slot }) => slot === "afternoon");
  const lists = hasMorningAndAfternoon ? pointsBySlot : [{ slot: undefined, points: row.points }];
  let sequence = 0;
  return <>{lists.map(({ slot, points }) => (
    <div className="a4xcap-list" key={slot ?? "all"}>
      {slot ? <span className="sxw">【{LEGACY_SLOT_LABELS[slot]}】</span> : null}
      {points.map((point, index) => {
        sequence += 1;
        return (
          <span className="legacy-overview-point" key={`${point.title}-${point.sortOrder}-${index}`}>
            <span className={`bh${sequence}`} />
            <span className="tt">{point.title}{point.categoryLabel ? <i className={point.categoryLabel === "企业" ? "qiye" : "feiyi"} /> : null}</span>
            {index < points.length - 1 ? <span className="jt" /> : null}
          </span>
        );
      })}
    </div>
  ))}</>;
}

function LegacyDay({ day, includeDayImages }: { day: PublicDay; includeDayImages: boolean }) {
  const content = dayContent(day);
  const gallery = includeDayImages ? day.nodes
    .filter((node) => node.nodeType === "point" && !node.categoryLabel && Boolean(node.imageUrl?.trim()))
    .slice(0, 6) : [];
  const galleryRows = Array.from({ length: Math.ceil(gallery.length / 3) }, (_, rowIndex) => (
    gallery.slice(rowIndex * 3, rowIndex * 3 + 3)
  ));
  return (
    <section className="legacy-route-day" aria-label={`Day${day.dayNumber}详情`}>
      <div className="day-t1">
        <span className="iimg"><img src={`${ASSET_ROOT}/xl18-26.png`} alt="" /></span>
        <span className="name">Day{day.dayNumber}</span>
        <span className="date">（{legacyDayDate(day.dayDate)}）</span>
        <span className="xl">{legacyDayTitle(day)}</span>
      </div>
      <div className="did-t2">
        <span className="iimg"><img src={`${ASSET_ROOT}/xl18-36.png`} alt="" /></span>
        <span className="name">住宿</span>
        <span className="dd">{accommodationForDay(day)}</span>
      </div>
      <div className="con newContent text-30px">
        {content ? <ItineraryMarkdown content={pdfLineBreaks(content)} /> : null}
        {gallery.length ? (
          <div className="a4piclist">
            {galleryRows.map((row, rowIndex) => (
              <div className="a4picrow" key={`row-${rowIndex}`}>
                {row.map((point, columnIndex) => {
                  const index = rowIndex * 3 + columnIndex;
                  return (
                    <figure className="picwp" key={`${point.title}-${point.sortOrder}-${index}`}>
                      <img src={resolveHttpResourceUrl(legacyGalleryImage(point.imageUrl ?? ""))} alt={point.title} />
                      <figcaption className="pictit"><span>{GALLERY_SIGNS[index]}</span>{point.title}</figcaption>
                    </figure>
                  );
                })}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function buildOverviewRows(days: PublicDay[]): OverviewRow[] {
  const rows: OverviewRow[] = [];
  for (const day of days) {
    const points = overviewPoints(day);
    const previous = rows.at(-1);
    if (previous && previous.lastDayNumber + 1 === day.dayNumber && overviewSignature(previous.firstDay, previous.points) === overviewSignature(day, points)) {
      previous.lastDayNumber = day.dayNumber;
      continue;
    }
    rows.push({ firstDay: day, firstDayNumber: day.dayNumber, lastDayNumber: day.dayNumber, points });
  }
  return rows;
}

function overviewPoints(day: PublicDay): PublicItineraryNode[] {
  return day.nodes
    .filter((node) => node.nodeType === "point")
    .sort((left, right) => slotRank(left.timeSlot) - slotRank(right.timeSlot) || left.sortOrder - right.sortOrder);
}

function overviewSignature(day: PublicDay, points: PublicItineraryNode[]): string {
  return JSON.stringify([
    legacyDayTitle(day),
    accommodationForDay(day),
    points.map((point) => [point.timeSlot, point.title, point.categoryLabel]),
  ]);
}

function overviewDayLabel(row: OverviewRow): string {
  return row.firstDayNumber === row.lastDayNumber
    ? `第${row.firstDayNumber}天`
    : `第${row.firstDayNumber}~${row.lastDayNumber}天`;
}

function overviewDayDate(value?: string | null): string {
  const normalized = value?.trim();
  if (!normalized) return "日期待定";
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:$|T)/.exec(normalized);
  if (!match) return normalized;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    Number.isNaN(date.getTime())
    || date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) return normalized;
  return `${month}月${day}日 周${"日一二三四五六"[date.getUTCDay()]}`;
}

function estimateOverviewHeight(rows: OverviewRow[]): number {
  return rows.reduce((total, row) => total + estimateOverviewRows(row), 0) * OVERVIEW_ROW_HEIGHT;
}

function estimateOverviewRows(row: OverviewRow): number {
  if (!row.points.length) return 1;
  const morning = row.points.filter((point) => point.timeSlot === "morning");
  const afternoon = row.points.filter((point) => point.timeSlot === "afternoon");
  if (morning.length && afternoon.length) {
    const morningWidth = estimatePointListWidth(morning) + 120;
    const morningRows = Math.max(1, Math.ceil(morningWidth / OVERVIEW_CONTENT_WIDTH));
    // 旧页计算下午行数时误用了上午宽度；保留该结果以复刻分页边界。
    const afternoonRows = Math.max(1, Math.ceil((morningWidth + 120) / OVERVIEW_CONTENT_WIDTH));
    const remaining = row.points.filter(
      (point) => point.timeSlot !== "morning" && point.timeSlot !== "afternoon",
    );
    const remainingRows = remaining.length
      ? Math.max(1, Math.ceil((estimatePointListWidth(remaining) + 120) / OVERVIEW_CONTENT_WIDTH))
      : 0;
    return morningRows + afternoonRows + remainingRows;
  }
  return Math.max(1, Math.ceil((estimatePointListWidth(row.points) + 120) / OVERVIEW_CONTENT_WIDTH));
}

function estimatePointListWidth(points: PublicItineraryNode[]): number {
  return points.reduce((total, point, index) => total
    + 30
    + Array.from(point.title).length * 31
    + (point.categoryLabel ? 73 : 0)
    + (index < points.length - 1 ? 30 : 0), 0);
}

function accommodationForDay(day: PublicDay): string {
  return day.stayAreaName?.trim()
    || day.nodes.find((node) => node.nodeType === "stay_area")?.title.trim()
    || "待确认";
}

function legacyDayTitle(day: PublicDay): string {
  const title = day.dayTitle?.trim();
  return !title || /^day\s*\d+$/i.test(title) ? `第${day.dayNumber}天` : title;
}

function dayContent(day: PublicDay): string {
  const narrative = buildDayNarratives(day).flatMap((part) => part.paragraphs);
  const segmentDescriptions = day.segments
    .filter((segment) => segment.transportMode !== "walk" && !segment.isInterDay && Boolean(segment.description?.trim()))
    .map((segment) => segment.description?.trim() ?? "");
  return [...narrative.map((part) => part.content), ...segmentDescriptions].join("\n\n");
}

function legacyDayDate(value?: string | null): string {
  if (!value) return "日期待定";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return `${value} 星期${"日一二三四五六"[date.getDay()]}`;
}

function legacyGalleryImage(value: string): string {
  return `${value}${value.includes("?") ? "&" : "?"}imageView2/1/w/446/h/251`;
}

function legacyHeaderImages(seed: string, configured?: string[]): [string, string] {
  const hash = Array.from(seed).reduce((total, character) => (total * 31 + character.charCodeAt(0)) >>> 0, 0);
  const first = hash % 11 + 1;
  const second = first % 11 + 1;
  return [
    configured?.[0]?.trim() || `${ASSET_ROOT}/pdf-head/${first}.jpg`,
    configured?.[1]?.trim() || `${ASSET_ROOT}/pdf-head/${second}.jpg`,
  ];
}

function slotRank(slot: PublicTimeSlot): number {
  return slot === "morning" ? 0 : slot === "afternoon" ? 1 : slot === "evening" ? 2 : 3;
}
